import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import styledComponent from '../../utils/styledComponent';
import {
  GetOpsLocationsFulfillmentCenters,
  CreateNextmv,
  GetTrafficConfiguration,
  UpdateTrafficConfiguration,
} from '../../libs/GraphQL';
import ConfirmationModal from '../../components/ConfirmationModal';
import DateField from '../../components/atoms/forms/DateField';
import MultiSelect from '../../components/atoms/forms/MultiSelect';
import Button from '../../components/atoms/forms/Button';
import yyyymmdd from '../../libs/date_convert';
import FCSectionCard from './FCSectionCard';
import SubmissionSummary from './SubmissionSummary';
import theme from '../../theme';
import validateMagnifier from './validation';
import {
  DAY_ABBREV,
  resolveFcCodes,
  formatTrafficConfigForApi,
  formatTrafficConfigForDisplay,
} from './utils';
import { PRETTY_TOAST_OPTS as toastDefaultOptions } from '../../utils/toast';

// Simple ID generator for internal vehicle config tracking
const generateConfigId = () =>
  `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const StyledDiv = styledComponent('div', {
  '& .container': {
    padding: theme.spacing.pagePadding,
  },
  '& .page-header': {
    ...theme.typography.pageTitle,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.pageTitleMargin,
    paddingBottom: theme.spacing.fieldGap,
    borderBottom: theme.borders.card,
  },
  '& .config-card': {
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
    padding: theme.spacing.cardPadding,
    marginBottom: theme.spacing.sectionGap,
  },
  '& .config-title': {
    ...theme.typography.sectionTitle,
    color: theme.colors.text,
    marginBottom: theme.spacing.sectionGap,
  },
  '& .filter-row': {
    display: 'flex',
    gap: theme.spacing.sectionGap,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  '& .input-wrapper': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .input-label': {
    ...theme.typography.body,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .required-indicator': {
    color: theme.colors.danger,
    marginLeft: theme.spacing.requiredGap,
  },
  '& .empty-state': {
    textAlign: 'center',
    padding: theme.spacing.emptyStatePadding,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
  },
  '& .button-container': {
    marginTop: theme.spacing.pageTitleMargin,
    textAlign: 'center',
  },
  '& .mv-table': {
    textAlign: 'center',
  },
  '& .mv-table th, & .mv-table td': {
    textAlign: 'center',
  },
  '@media (max-width: 600px)': {
    '& .filter-row': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
});

function ViewNextmv() {
  const [dropOffDate, setDropOffDate] = useState(() => new Date());
  const [fulfillmentCentersList, setFulfillmentCentersList] = useState([]);
  const [fcTimezoneMap, setFcTimezoneMap] = useState({});
  const [selectedFCs, setSelectedFCs] = useState([]);
  const [vehicleConfigs, setVehicleConfigs] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isInBlockedTime, setIsInBlockedTime] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  // Cooldown state (10 minutes after successful submission)
  const [cooldownEndTime, setCooldownEndTime] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  // Traffic configuration state
  const [trafficConfigs, setTrafficConfigs] = useState({});
  const [originalTrafficConfigs, setOriginalTrafficConfigs] = useState({});
  const [trafficConfigsExpanded, setTrafficConfigsExpanded] = useState({});
  const [trafficConfigsLoading, setTrafficConfigsLoading] = useState({});

  const checkBlockedTimeWindow = () => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const currentHour = Number.parseInt(
      parts.find((p) => p.type === 'hour').value,
      10,
    );
    const currentMinute = Number.parseInt(
      parts.find((p) => p.type === 'minute').value,
      10,
    );
    return currentHour === 17 && currentMinute >= 30 && currentMinute <= 50;
  };

  // Format remaining cooldown time as mm:ss
  const formatCooldownTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDropOffDateChange = (value) => {
    setDropOffDate(value);
  };

  const handleFCChange = (selectedOptions) => {
    const fcValues = selectedOptions?.map((opt) => opt.value) || [];
    setSelectedFCs(fcValues);

    setVehicleConfigs((prevConfigs) => {
      const newConfigs = {};

      // Keep existing configs for still-selected FCs and add defaults for new ones
      fcValues.forEach((fc) => {
        if (prevConfigs[fc]) {
          newConfigs[fc] = prevConfigs[fc];
        } else {
          const datePrefix = yyyymmdd(dropOffDate, '-');
          newConfigs[fc] = [
            {
              id: generateConfigId(),
              vehicleType: 'AM',
              numVehicles: 1,
              capacity: '70',
              shiftStartTime: `${datePrefix}T04:00`,
              targetShiftEndTime: `${datePrefix}T14:30`,
              shiftEndTime: `${datePrefix}T16:00`,
              lat: '',
              long: '',
              skillSet: 'Daytime',
            },
          ];
        }
      });

      return newConfigs;
    });

    // Clear traffic state for deselected FCs
    setTrafficConfigs((prev) => {
      const cleaned = {};
      fcValues.forEach((fc) => {
        if (prev[fc]) cleaned[fc] = prev[fc];
      });
      return cleaned;
    });
    setOriginalTrafficConfigs((prev) => {
      const cleaned = {};
      fcValues.forEach((fc) => {
        if (prev[fc]) cleaned[fc] = prev[fc];
      });
      return cleaned;
    });
    setTrafficConfigsExpanded((prev) => {
      const cleaned = {};
      fcValues.forEach((fc) => {
        if (prev[fc] !== undefined) cleaned[fc] = prev[fc];
      });
      return cleaned;
    });
    setTrafficConfigsLoading((prev) => {
      const cleaned = {};
      fcValues.forEach((fc) => {
        if (prev[fc] !== undefined) cleaned[fc] = prev[fc];
      });
      return cleaned;
    });

    // Fetch traffic config for newly selected FCs
    fcValues.forEach((fc) => {
      if (!trafficConfigs[fc]) {
        fetchTrafficConfig(fc);
      }
    });
  };

  const handleVehicleFieldChange = (fc, vehicleId, field, value) => {
    setVehicleConfigs((prevConfigs) => ({
      ...prevConfigs,
      [fc]: prevConfigs[fc].map((vehicle) =>
        vehicle.id === vehicleId ? { ...vehicle, [field]: value } : vehicle,
      ),
    }));
  };

  const addVehicleType = (fc, shiftType) => {
    const shiftDefaults = {
      AM: {
        start: '04:00',
        targetEnd: '14:30',
        end: '16:00',
      },
      PM: {
        start: '14:00',
        targetEnd: '20:30',
        end: '22:00',
      },
    };

    const defaults = shiftDefaults[shiftType] || shiftDefaults.AM;
    const datePrefix = yyyymmdd(dropOffDate, '-');

    const newVehicle = {
      id: generateConfigId(),
      vehicleType: shiftType,
      numVehicles: 1,
      capacity: '70',
      shiftStartTime: `${datePrefix}T${defaults.start}`,
      targetShiftEndTime: `${datePrefix}T${defaults.targetEnd}`,
      shiftEndTime: `${datePrefix}T${defaults.end}`,
      lat: '',
      long: '',
      skillSet: shiftType === 'AM' ? 'Daytime' : 'Overnight',
    };

    setVehicleConfigs((prevConfigs) => ({
      ...prevConfigs,
      [fc]: [...(prevConfigs[fc] || []), newVehicle],
    }));
  };

  const removeVehicle = (fc, vehicleId) => {
    setVehicleConfigs((prevConfigs) => ({
      ...prevConfigs,
      [fc]: prevConfigs[fc].filter((v) => v.id !== vehicleId),
    }));
  };

  // Traffic configuration functions
  // Convert object with numeric keys to sorted array (API returns trafficMultiplier, we use magnifier)
  const convertTrafficConfigObject = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.values(obj)
      .map((item) => ({
        startTime: item.startTime,
        endTime: item.endTime,
        magnifier: item.trafficMultiplier,
      }))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const fetchTrafficConfig = async (fc) => {
    if (trafficConfigs[fc]) return; // Skip if already loaded

    const emptyConfig = { weekday: [], weekend: [] };

    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: true }));
    try {
      const result = await GetTrafficConfiguration(fc);
      // Parse trafficConfiguration - it may be a JSON string or already parsed
      let parsedConfig = result?.trafficConfiguration;
      if (typeof parsedConfig === 'string') {
        parsedConfig = JSON.parse(parsedConfig);
      }
      // Convert from object with numeric keys to array format
      const config = {
        weekday: convertTrafficConfigObject(parsedConfig?.weekday),
        weekend: convertTrafficConfigObject(parsedConfig?.weekend),
      };
      setOriginalTrafficConfigs((prev) => ({ ...prev, [fc]: config }));
      setTrafficConfigs((prev) => ({ ...prev, [fc]: config }));
      setTrafficConfigsExpanded((prev) => ({ ...prev, [fc]: true }));
    } catch (e) {
      console.error(`Failed to fetch traffic config for ${fc}:`, e);
      toast.error(
        `Failed to load traffic configuration for ${fc}`,
        toastDefaultOptions,
      );
      setTrafficConfigs((prev) => ({ ...prev, [fc]: emptyConfig }));
      setOriginalTrafficConfigs((prev) => ({ ...prev, [fc]: emptyConfig }));
    }
    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: false }));
  };

  const handleTrafficConfigChange = (fc, dayType, windows) => {
    setTrafficConfigs((prev) => ({
      ...prev,
      [fc]: {
        ...prev[fc],
        [dayType]: windows,
      },
    }));
  };

  const toggleTrafficConfigExpanded = (fc) => {
    setTrafficConfigsExpanded((prev) => ({ ...prev, [fc]: !prev[fc] }));
  };

  const hasTrafficConfigModifications = (fc) => {
    const original = originalTrafficConfigs[fc];
    const current = trafficConfigs[fc];
    if (!original || !current) return false;
    return JSON.stringify(original) !== JSON.stringify(current);
  };

  const resetTrafficConfigToDefaults = (fc) => {
    const original = originalTrafficConfigs[fc];
    if (original) {
      setTrafficConfigs((prev) => ({
        ...prev,
        [fc]: JSON.parse(JSON.stringify(original)),
      }));
    }
  };

  const validateTrafficConfigForFC = (fc) => {
    const trafficConfig = trafficConfigs[fc] || { weekday: [], weekend: [] };
    const allWindows = [
      ...(trafficConfig.weekday || []).map((w) => ({
        ...w,
        dayType: 'weekday',
      })),
      ...(trafficConfig.weekend || []).map((w) => ({
        ...w,
        dayType: 'weekend',
      })),
    ];

    const hasError = allWindows.some((window) => {
      const { valid, error } = validateMagnifier(window.magnifier);
      if (!valid) {
        toast.error(
          `${fc}: ${error} for ${window.startTime} (${window.dayType})`,
          toastDefaultOptions,
        );
        return true;
      }
      return false;
    });

    return !hasError;
  };

  const handleSaveTrafficConfig = async (fc) => {
    if (!validateTrafficConfigForFC(fc)) {
      return;
    }

    const formattedTrafficConfig = formatTrafficConfigForApi(
      fc,
      trafficConfigs[fc],
    );

    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: true }));

    try {
      await UpdateTrafficConfiguration(formattedTrafficConfig);

      // Update original configs to reflect the saved state
      setOriginalTrafficConfigs((prev) => ({
        ...prev,
        [fc]: JSON.parse(JSON.stringify(trafficConfigs[fc])),
      }));

      toast.success(
        `Traffic configuration for ${fc} saved successfully!`,
        toastDefaultOptions,
      );
    } catch (e) {
      console.error(`Failed to save traffic config for ${fc}:`, e);
      toast.error(
        `Failed to save traffic configuration for ${fc}`,
        toastDefaultOptions,
      );
    }

    setTrafficConfigsLoading((prev) => ({ ...prev, [fc]: false }));
  };

  const getFulfillmentCenters = async () => {
    setIsLoading(true);

    try {
      // Fetch all locations using pagination
      const allLocations = [];
      let nextToken = null;

      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await GetOpsLocationsFulfillmentCenters(nextToken);
        const locations = response?.data?.opsLocations?.locations || [];
        allLocations.push(...locations);
        nextToken = response?.data?.opsLocations?.nextToken;
      } while (nextToken);

      // Extract unique FCs and their timezones
      const fcMap = {};
      allLocations.forEach((location) => {
        if (
          location.fulfillmentCenter &&
          location.fulfillmentCenter !== 'Does Not Exist'
        ) {
          fcMap[location.fulfillmentCenter] = location.timeZone || '';
        }
      });

      setFcTimezoneMap(fcMap);

      const formattedCenters = Object.keys(fcMap)
        .sort((a, b) => a.localeCompare(b))
        .map((fc) => ({ value: fc, label: fc }));

      setFulfillmentCentersList(formattedCenters);
    } catch (e) {
      console.error(e);
      toast.error('Failed fetching Fulfillment Centers', toastDefaultOptions);
    }

    setIsLoading(false);
  };

  const validateAllFields = () => {
    const hasError = selectedFCs.some((fc) => {
      if (!vehicleConfigs[fc] || vehicleConfigs[fc].length === 0) {
        toast.error(
          `Please add at least one vehicle for ${fc}`,
          toastDefaultOptions,
        );
        return true;
      }

      const vehicleError = vehicleConfigs[fc].some((vehicle) => {
        if (
          !vehicle.vehicleType ||
          !vehicle.capacity ||
          !vehicle.shiftStartTime ||
          !vehicle.targetShiftEndTime ||
          !vehicle.shiftEndTime ||
          !vehicle.lat ||
          !vehicle.long
        ) {
          toast.error(
            `Please fill all required fields for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        if (Number.isNaN(Number.parseFloat(vehicle.capacity))) {
          toast.error(
            `Capacity must be a number for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        if (
          Number.isNaN(Number.parseFloat(vehicle.lat)) ||
          Number.isNaN(Number.parseFloat(vehicle.long))
        ) {
          toast.error(
            `Latitude and Longitude must be numbers for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        const lat = Number.isNaN(Number.parseFloat(vehicle.lat))
          ? Number.NaN
          : Number.parseFloat(vehicle.lat);
        const long = Number.isNaN(Number.parseFloat(vehicle.long))
          ? Number.NaN
          : Number.parseFloat(vehicle.long);

        if (lat < -90 || lat > 90) {
          toast.error(
            `Latitude must be between -90 and 90 for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        if (long < -180 || long > 180) {
          toast.error(
            `Longitude must be between -180 and 180 for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        const numVehicles = Number.parseInt(vehicle.numVehicles, 10);
        if (Number.isNaN(numVehicles) || numVehicles < 1) {
          toast.error(
            `Number of vehicles must be at least 1 for vehicles in ${fc}`,
            toastDefaultOptions,
          );
          return true;
        }

        return false;
      });

      if (vehicleError) return true;

      // Traffic configuration validation
      return !validateTrafficConfigForFC(fc);
    });

    return !hasError;
  };

  // Generate vehicle ID in format: {stateCode}_{cityCode}_{dayOfWeek}_{amOrPm}_{sequenceNumber}
  // Example: ca_la_tue_pm_1, il_chi_tue_am_2
  const generateVehicleId = (
    fc,
    vehicleType,
    sequenceNumber,
    dropOffDateValue,
  ) => {
    const { stateCode, cityCode } = resolveFcCodes(fc);
    const dayOfWeek = DAY_ABBREV[dropOffDateValue.getDay()];
    const amOrPm = vehicleType.toLowerCase();

    return `${stateCode}_${cityCode}_${dayOfWeek}_${amOrPm}_${sequenceNumber}`;
  };

  const prepareSubmissionData = () => {
    const formattedDropOffDate = yyyymmdd(dropOffDate, '-');
    const submissionData = [];

    // Track sequence numbers per {stateCode}_{cityCode}_{dayOfWeek}_{amOrPm} combination
    const sequenceCounters = {};

    // Helper to get and increment sequence number for a combination
    const getNextSequenceNumber = (fc, vehicleType) => {
      const { stateCode, cityCode } = resolveFcCodes(fc);
      const dayOfWeek = DAY_ABBREV[dropOffDate.getDay()];
      const amOrPm = vehicleType.toLowerCase();

      const key = `${stateCode}_${cityCode}_${dayOfWeek}_${amOrPm}`;
      sequenceCounters[key] = (sequenceCounters[key] || 0) + 1;
      return sequenceCounters[key];
    };

    // Create a submission for each fulfillment center
    selectedFCs.forEach((fc) => {
      const vehicles = [];

      vehicleConfigs[fc].forEach((config) => {
        const numVehicles = Number.parseInt(config.numVehicles, 10) || 1;

        for (let i = 0; i < numVehicles; i += 1) {
          const sequenceNumber = getNextSequenceNumber(fc, config.vehicleType);
          const vehicleId = generateVehicleId(
            fc,
            config.vehicleType,
            sequenceNumber,
            dropOffDate,
          );
          vehicles.push({
            id: vehicleId,
            capacity: Number.parseInt(config.capacity, 10),
            shiftStartTime: `${config.shiftStartTime}:00`,
            targetShiftEndTime: `${config.targetShiftEndTime}:00`,
            shiftEndTime: `${config.shiftEndTime}:00`,
            lat: Number.parseFloat(config.lat),
            long: Number.parseFloat(config.long),
            skillSet: config.vehicleType === 'AM' ? 'Daytime' : 'Overnight',
          });
        }
      });

      // Format traffic configuration for the confirmation summary (keep
      // endTime for display); endTime is stripped before the API call.
      const formattedTrafficConfig = formatTrafficConfigForDisplay(
        fc,
        trafficConfigs[fc],
      );

      submissionData.push({
        dropOffDate: formattedDropOffDate,
        fulfillmentCenter: fc,
        runType: 'InitialRun',
        vehicles,
        trafficConfiguration: formattedTrafficConfig,
      });
    });

    return {
      dropOffDate: formattedDropOffDate,
      fulfillmentCenters: selectedFCs,
      vehicles: submissionData,
    };
  };

  const handleGenerateRun = () => {
    if (!selectedFCs.length) {
      toast.error(
        'Please select at least one fulfillment center',
        toastDefaultOptions,
      );
      return;
    }

    if (!validateAllFields()) {
      return;
    }

    const data = prepareSubmissionData();
    setConfirmationData(data);
    setShowConfirmModal(true);
  };

  const handleSubmit = async () => {
    if (!confirmationData) return;
    setIsSubmitting(true);
    try {
      // Submit each FC's run with trafficConfiguration (strip endTime for API)
      const submissions = confirmationData.vehicles.map((vehicleRun) => {
        const trafficConfigForApi = (vehicleRun.trafficConfiguration || []).map(
          ({ endTime, ...rest }) => rest,
        );
        return CreateNextmv({
          ...vehicleRun,
          trafficConfiguration: trafficConfigForApi,
        });
      });
      const results = await Promise.all(submissions);

      // Log results and check for errors
      const failedRuns = results.filter((result) => {
        const statusCode = result.data?.createNextmvRun?.statusCode;
        return statusCode !== 200 && statusCode !== '200';
      });

      if (failedRuns.length > 0) {
        const errorMessages = failedRuns
          .map((r) => r.data?.createNextmvRun?.message || 'Unknown error')
          .join('; ');
        throw new Error(`Some runs failed: ${errorMessages}`);
      }

      const runIds = results
        .map((r) => r.data?.createNextmvRun?.nextmvRunId)
        .filter(Boolean);
      const runIdText =
        runIds.length > 0 ? ` Run ID(s): ${runIds.join(', ')}` : '';
      toast.success(
        `Run generation initiated successfully.${runIdText}`,
        toastDefaultOptions,
      );

      // Set 10-minute cooldown
      const cooldownDuration = 10 * 60 * 1000; // 10 minutes in milliseconds
      setCooldownEndTime(Date.now() + cooldownDuration);

      // Reset form
      setDropOffDate(new Date());
      setSelectedFCs([]);
      setVehicleConfigs({});
      setTrafficConfigs({});
      setOriginalTrafficConfigs({});
      setTrafficConfigsExpanded({});
      setShowConfirmModal(false);
      setConfirmationData(null);
    } catch (e) {
      console.error(e);
      toast.error(`Submission failed. ${e.message || ''}`, toastDefaultOptions);
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    getFulfillmentCenters();
    setIsInBlockedTime(checkBlockedTimeWindow());

    const interval = setInterval(() => {
      setIsInBlockedTime(checkBlockedTimeWindow());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Cooldown timer effect
  useEffect(() => {
    if (!cooldownEndTime) {
      setCooldownRemaining(0);
      return undefined;
    }

    const updateCooldown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((cooldownEndTime - now) / 1000));
      setCooldownRemaining(remaining);

      if (remaining === 0) {
        setCooldownEndTime(null);
      }
    };

    // Update immediately
    updateCooldown();

    // Update every second
    const cooldownInterval = setInterval(updateCooldown, 1000);

    return () => clearInterval(cooldownInterval);
  }, [cooldownEndTime]);

  const isInCooldown = cooldownRemaining > 0;
  const isGenerateRunDisabled =
    isLoading || selectedFCs.length === 0 || isInBlockedTime || isInCooldown;

  const getGenerateButtonText = () => {
    if (isSubmitting) return 'Generating...';
    if (isInCooldown)
      return `Generate Run (Cooldown: ${formatCooldownTime(
        cooldownRemaining,
      )})`;
    return 'Generate Run';
  };

  return (
    <StyledDiv>
      <div className="container">
        <h1 className="page-header">Nextmv</h1>

        {/* Run Configuration */}
        <div className="config-card">
          <h2 className="config-title">Run Configuration</h2>
          <div className="filter-row">
            <div className="input-wrapper">
              <div className="input-label">
                Drop Off Date<span className="required-indicator">*</span>
              </div>
              <DateField
                onChange={handleDropOffDateChange}
                value={dropOffDate}
                minDate={new Date()}
                disabled={isSubmitting}
              />
            </div>
            <div className="input-wrapper">
              <div className="input-label">
                Fulfillment Centers<span className="required-indicator">*</span>
              </div>
              <MultiSelect
                options={fulfillmentCentersList}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                onChange={handleFCChange}
                isLoading={isLoading}
                isDisabled={isLoading}
                value={fulfillmentCentersList.filter((opt) =>
                  selectedFCs.includes(opt.value),
                )}
                maxMenuHeight={400}
                width="350px"
              />
            </div>
          </div>
        </div>

        {/* FC Vehicle Sections */}
        <div className="fc-sections-container">
          {selectedFCs.length === 0 ? (
            <div className="empty-state">
              Please select at least one Fulfillment Center to configure vehicle
              information.
            </div>
          ) : (
            selectedFCs.map((fc) => (
              <FCSectionCard
                key={fc}
                fc={fc}
                timezone={fcTimezoneMap[fc]}
                vehicles={vehicleConfigs[fc]}
                onAddVehicle={(shiftType) => addVehicleType(fc, shiftType)}
                onRemoveVehicle={(vehicleId) => removeVehicle(fc, vehicleId)}
                onVehicleChange={(vehicleId, field, value) =>
                  handleVehicleFieldChange(fc, vehicleId, field, value)
                }
                isSubmitting={isSubmitting}
                trafficConfig={trafficConfigs[fc]}
                originalTrafficConfig={originalTrafficConfigs[fc]}
                trafficConfigExpanded={trafficConfigsExpanded[fc]}
                trafficConfigLoading={trafficConfigsLoading[fc]}
                onToggleTrafficExpanded={() => toggleTrafficConfigExpanded(fc)}
                onTrafficConfigChange={(dayType, windows) =>
                  handleTrafficConfigChange(fc, dayType, windows)
                }
                onSaveTrafficConfig={() => handleSaveTrafficConfig(fc)}
                onResetTrafficConfig={() => resetTrafficConfigToDefaults(fc)}
                hasTrafficModifications={hasTrafficConfigModifications(fc)}
              />
            ))
          )}
        </div>

        {/* Generate Run Button */}
        <div className="button-container">
          <Button
            variant="primary-lg"
            onClick={handleGenerateRun}
            disabled={isGenerateRunDisabled}
          >
            {getGenerateButtonText()}
          </Button>
        </div>
      </div>

      <ConfirmationModal
        title="Confirm Vehicle Configuration Submission"
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={() => handleSubmit()}
        loading={isSubmitting}
      >
        {confirmationData && <SubmissionSummary data={confirmationData} />}
      </ConfirmationModal>
    </StyledDiv>
  );
}

export default ViewNextmv;
